import { IPaypalWidgetData, IPaypalWidgetStyle, IPayPayActionType, ISubscriptionBillingCycleUnit } from "@pageBuilder/typings";
import { WidgetRendererBase } from "./widget-renderer-base";

export class PaypalWidgetRenderer extends WidgetRendererBase<IPaypalWidgetData> {

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("paypalWidget");
    }

    protected renderInternal(element: HTMLElement, data: IPaypalWidgetData): HTMLElement {
        this.clearAlignmentClasses(element);

        let content = element.querySelector(".widget-content") as HTMLElement;
        if (!content) {
            content = this.createContentElement();
            element.appendChild(content);
        }

        if (this.isEditorRendering) {
            this.renderPlaceholder(element, data);
        } else {
            this.renderPayPalButtonSnippet(element, data);
        }

        this.renderStyles(element, data);

        return element;
    }

    protected renderStyles(element: HTMLElement, data: IPaypalWidgetData): void {
        this.renderBaseStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style: IPaypalWidgetStyle | null): void {
        element.classList.add("alignable");
        element.classList.add(style?.alignment ? `justify-${style.alignment}` : "justify-center");
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private clearAlignmentClasses(element: HTMLElement): void {
        element.classList.remove("alignable", "justify-start", "justify-center", "justify-end");
    }

    private renderPlaceholder(element: HTMLElement, data: IPaypalWidgetData): void {
        this.ensureWidgetContent(element);
        const paypalImage = element.querySelector(".widget-content .editor-img-wrapper img");
        if (paypalImage) {
            if (paypalImage.getAttribute("data-action") !== data.action) {
                paypalImage.setAttribute("src", PaypalWidgetRenderer.getImageUrl(data.action));
                paypalImage.setAttribute("data-action", data.action);
            }
        } else {
            const content = element.querySelector(".widget-content");
            const wrapper = document.createElement("div");
            const img = document.createElement("img");
            wrapper.classList.add("editor-img-wrapper");
            img.src = PaypalWidgetRenderer.getImageUrl(data.action);
            wrapper.append(img);
            content?.appendChild(wrapper);
        }
    }

    private ensureWidgetContent(element: HTMLElement): void {
        const content = element.querySelector(".widget-content");
        if (!content) {
            const content = document.createElement("div");
            content.classList.add("widget-content");
            element.append(content);
        }
    }

    private renderPayPalButtonSnippet(element: HTMLElement, snippet: IPaypalWidgetData): void {
        const content = element.querySelector(".widget-content");
        if (!content) {
            return;
        }

        content.innerHTML = `
            <div id="${element.id}-form-wrapper" data-tax="${snippet.tax}">
                <form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_blank">
                ${this.getParamsHtml(snippet)}
                <input type="image" src="${PaypalWidgetRenderer.getImageUrl(snippet.action)}" border="0" name="submit" alt="PayPal - The safer, easier way to pay online!">
                <img alt="" border="0" src="https://www.paypalobjects.com/en_US/i/scr/pixel.gif" width="1" height="1">
            </form>
            </div>
        `;
    }

    private getParamsHtml(data: IPaypalWidgetData): string {
        let res = "";
        const args = this.getParams(data);
        let pair = args.next();
        while (!pair.done) {
            const [key, val] = pair.value;
            if (val !== undefined && val !== null) {
                res += `<input type="hidden" name="${key}" value="${val}" />`;
            }
            pair = args.next();
        }
        return res;
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    private* getParams(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        yield ["business", data.merchantId];
        yield ["lc", "US"];
        const inner = this.getParamsRaw(data);
        let pair = inner.next();
        while (!pair.done) {
            yield pair.value;
            pair = inner.next();
        }
        yield ["return", data.returnUrl];
    }

    private getParamsRaw(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        switch (data.action) {
            case "buynow":
                return PaypalWidgetRenderer.getBuyParams(data);
            case "cart":
                return PaypalWidgetRenderer.getCartParams(data);
            case "donate":
                return PaypalWidgetRenderer.getDonateParams(data);
            case "subscribe":
                return this.getSubscribeParams(data);
            default:
                return PaypalWidgetRenderer.getBuyParams(data);
        }
    }

    private static* getBuyParams(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        yield ["cmd", "_xclick"];
        yield ["item_name", data.item.name];
        yield ["item_number", data.item.sku];
        yield ["amount", data.item.price.amount];
        yield ["currency_code", data.item.price.currency];
        yield ["button_subtype", "services"];
        yield ["no_note", "0"];
        yield ["tax_rate", data.tax];
        yield ["shipping", data.shipping];
        yield ["bn", "PP-BuyNowBF:btn_buynowCC_LG.gif:NonHostedGuest"];
    }

    private static* getCartParams(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        yield ["cmd", "_cart"];
        yield ["item_name", data.item.name];
        yield ["item_number", data.item.sku];
        yield ["amount", data.item.price.amount];
        yield ["currency_code", data.item.price.currency];
        yield ["button_subtype", "products"];
        yield ["no_note", "0"];
        yield ["tax_rate", data.tax];
        yield ["shipping", data.shipping];
        yield ["add", "1"];
        yield ["bn", "PP-ShopCartBF:btn_cart_LG.gif:NonHostedGuest"];
    }

    private static* getDonateParams(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        yield ["cmd", "_donations"];
        yield ["item_name", data.item.name];
        yield ["amount", data.item.price.amount];
        yield ["currency_code", data.item.price.currency];
    }

    private* getSubscribeParams(data: IPaypalWidgetData): IterableIterator<[string, string | number | undefined]> {
        yield ["cmd", "_xclick-subscriptions"];
        yield ["item_name", data.item.name];
        yield ["item_number", data.item.sku];
        yield ["no_note", "1"];
        yield ["src", "1"];
        yield ["a3", data.item.price.amount];
        yield ["p3", data.item.period?.billingCycle.number];
        yield ["t3", this.getPeriodUnit(data.item.period?.billingCycle.unit)];
        yield ["currency_code", data.item.price.currency];
        yield ["srt", data.item.period?.cycles];
        yield ["bn", "PP-SubscriptionsBF:btn_subscribeCC_LG.gif:NonHostedGuest"];
    }

    private static getImageUrl(action: IPayPayActionType): string {
        switch (action) {
            case "buynow":
                return "https://www.paypalobjects.com/en_US/i/btn/btn_buynowCC_LG.gif";
            case "cart":
                return "https://www.paypalobjects.com/en_US/i/btn/btn_cart_LG.gif";
            case "donate":
                return "https://www.paypalobjects.com/en_US/i/btn/btn_donateCC_LG.gif";
            case "subscribe":
                return "https://www.paypalobjects.com/en_US/i/btn/btn_subscribeCC_LG.gif";
            default:
                return "https://www.paypalobjects.com/en_US/i/btn/btn_buynowCC_LG.gif";
        }
    }

    private getPeriodUnit(unit?: ISubscriptionBillingCycleUnit): string | undefined {
        switch (unit) {
            case "day":
                return "D";
            case "month":
                return "M";
            case "week":
                return "W";
            case "year":
                return "Y";
            default:
                return undefined;
        }
    }

}
